import { defineFeature } from '../define-feature';
import { BLOG_WEB_PATH, SeoSpaceTab, seoSpaceWebPath } from '../../seo/seo-space-routes';

export default defineFeature({
  id: 'marketing.seo_pack.overview',
  category: 'marketing',
  title: 'Comprendre et activer le pack référencement (Google et IA)',
  intent:
    'Faire rédiger et publier automatiquement 2 articles par semaine sur votre blog et obtenir chaque mois un article sur vous publié sur un site de votre secteur, pour être trouvé durablement sur Google et repris dans les réponses des assistants IA, sans écrire une ligne.',
  context: {
    why: "Vos clients vous cherchent sur Google, et de plus en plus en posant leur question à ChatGPT, Perplexity ou au mode IA de Google. Dans les deux cas, ce qui compte est le même : du contenu régulier qui répond vraiment aux questions qu'ils se posent, et d'autres sites qui parlent de vous. Un professionnel n'a ni le temps ni l'envie de produire ça. Le pack référencement, inclus dans la formule Max, s'en charge : chaque mois, il cherche les sujets que vos clients tapent réellement (votre métier, vos prestations, votre ville), vous les propose, rédige les articles retenus avec votre voix, les publie aux jours que vous avez choisis, et fait publier un article sur vous chez un site partenaire de votre secteur avec un lien vers votre site. Les articles sont écrits pour être lus aussi bien par Google que par les IA (réponse dès l'introduction, questions fréquentes balisées), et votre site leur est ouvert et décrit dans leur format. Vous gardez la main : rien n'est publié sans votre « oui ».",
    use_cases: [
      'Un institut qui veut apparaître sur « épilation définitive + sa ville » sans tenir un blog.',
      'Un pro qui veut être cité quand un client demande à ChatGPT « un bon ostéopathe à Nantes ».',
      "Un site tout neuf (ou migré) qui n'a encore aucune visibilité sur Google.",
      'Un pro qui publiait de temps en temps et veut une cadence régulière sans y penser.',
      'Un établissement multi-villes qui veut couvrir chacune de ses zones.',
    ],
  },
  required_scopes: ['website.seo_articles'],
  prerequisites: [
    {
      ref: 'company.profile.activities',
      requirement: 'required',
      why: 'Le moteur cherche les sujets à partir de votre activité déclarée.',
    },
    {
      ref: 'catalog.prestations.create',
      requirement: 'required',
      why: 'Au moins deux prestations qui nomment un vrai service : ce sont elles que vos clients recherchent.',
    },
    {
      ref: 'website.seo_geo.manage',
      requirement: 'recommended',
      why: "En référencement local, le moteur cherche vos sujets avec vos villes et zones d'intervention.",
    },
  ],
  ui_entry_points: [
    {
      route: BLOG_WEB_PATH,
      element:
        'Bandeau « Référencement » (suivi du mois en cours) en tête de la page Blog, bouton « Articles, backlinks et réglages »',
    },
    { route: seoSpaceWebPath(SeoSpaceTab.SETTINGS), element: 'Onglet « Réglages » de l’espace référencement' },
  ],
  api: {
    primary_operation_id: 'SeoContentAppController_pack',
    related_operation_ids: [
      'SeoContentAppController_timeline',
      'SeoContentAppController_onboardingSuggestions',
      'SeoContentAppController_completeOnboarding',
      'SeoContentAppController_getSettings',
      'SeoContentAppController_saveSettings',
      'ProSubscriptionAppController_getSubscriptionSummary',
    ],
  },
  steps: [
    "**Vérifiez votre formule** : le pack est inclus dans Max (Solo ou Équipe). Sur Starter ou Pro, passez à Max depuis la page Abonnement ; le pack n'est pas disponible pendant l'essai gratuit.",
    '**Ouvrez la page Blog** (Communication › Blog & référencement) : le bandeau « Référencement » en haut de page ouvre votre espace référencement.',
    '**Suivez le parcours de première activation** (« Configurer en 2 minutes ») : qui signe les articles, vos concurrents, qui relit les articles partenaires, vos sujets et vos jours de publication. Rien ne démarre avant.',
    '**Laissez tourner** : vos premiers sujets sont proposés pour le mois suivant, vous recevez une notification quand ils sont prêts à trier.',
    '**Chaque mois** : triez les sujets proposés (oui / non), lancez la rédaction, relisez les articles prêts. Le lien partenaire du mois se commande tout seul.',
  ],
  gotchas: [
    "**Le pack est bloqué tant que le parcours de première activation n'est pas suivi** : ni sujets, ni lien partenaire. Le bandeau du blog vous le rappelle.",
    "**Le premier mois du pack est toujours le mois qui suit l'activation**, pour les articles comme pour le lien partenaire. Activé le 15 octobre, le pack produit à partir de novembre.",
    "**Votre profil doit être assez complet** pour que le moteur cherche juste : une activité déclarée, au moins deux prestations qui nomment un vrai service, et une ville (zone d'intervention ou adresse d'établissement) si votre référencement est local. Sinon l'espace référencement affiche « Compléter mon profil » à la place du parcours.",
    "**Rien n'est publié sans vous** : les sujets attendent votre oui, les articles attendent votre relecture, et pour l'article partenaire vous choisissez entre publication automatique et relecture.",
    "**Un mois sans lancement n'est pas rattrapé** : si vous ne triez pas vos sujets, les dates du mois restent vides. Les sujets non tranchés sont reportés au mois suivant.",
    "**Si votre abonnement Max s'arrête**, le pack s'arrête à la fin de la période déjà payée, les options sont désactivées et les liens restants ne sont ni livrés ni remboursés.",
    "**Google et IA, même travail, pas la même mesure.** Votre site est ouvert aux robots des assistants IA et leur fournit une fiche à leur format (`llms.txt`) ; chaque article porte ses questions-réponses dans un balisage qu'ils lisent. C'est ce qu'on appelle le GEO (Generative Engine Optimization). Personne ne peut garantir ni compter une citation dans ChatGPT ou Perplexity ; côté Google, le suivi des résultats par article arrivera avec les statistiques du site.",
    "**L'onglet Réglages** regroupe vos jours et heure de publication (2 jours par semaine, 4 avec l'option « Doubler mes articles »), l'auteur des articles (l'établissement ou un membre ; changer d'auteur reconstruit sa voix), vos concurrents, les sujets à pousser ou à éviter, le ton, vos consignes et vos mots interdits.",
  ],
  related_features: [
    'marketing.seo_pack.topics',
    'marketing.seo_pack.articles',
    'marketing.seo_pack.backlinks',
    'marketing.seo_pack.options',
    'marketing.blog.article.create',
    'website.seo_geo.manage',
    'account.subscription.manage',
  ],
});
